export { S as StoryblokComponent, a as StoryblokComponentList } from './StoryblokComponent-server-DCJyvOF5.mjs';
export { S as StoryblokProvider } from './StoryblokProvider-shared-BsrrIAwo.mjs';
export { S as StoryblokStory } from './StoryblokStory-server-Wu2rwoY_.mjs';
export { d as defineStoryblok, a as getAdditionalDataHook, g as getBlokComponent, s as storyblokGlobalData } from './defineBloks-shared-oexg9VmF.mjs';
import { omitFalsy } from '@frend-digital/utils';
import { defu } from 'defu';

/* eslint-disable @typescript-eslint/no-explicit-any */ function getStoryLink(link) {
    let href;
    if ("cached_url" in link) {
        href = link.cached_url;
    }
    if ("url" in link && typeof link.url === "string" && !!link.url) {
        href = link.url;
    }
    if ("story" in link && link.story && "url" in link.story) {
        href = link.story.url;
    }
    if ("story" in link && link.story && "full_slug" in link.story && !!link.story.full_slug) {
        href = link.story.full_slug;
    }
    if (!href) return null;
    return {
        href: parseHref(href),
        rawHref: href,
        ...getAdditionalLinkParams(link),
        type: "url"
    };
}
function getLink(link) {
    if (!link) return null;
    if (typeof link === "string") {
        return {
            href: parseHref(link),
            rawHref: link,
            type: "url"
        };
    }
    // Escape empty links.
    if ("url" in link && link.url === "" && "id" in link && link.id === "" && link.linktype === "url") {
        return null;
    }
    if (link.linktype === "story" || "story" in link) {
        const result = getStoryLink(link);
        return result;
    }
    if (link.linktype === "url") {
        const href = link.url || link.cached_url;
        if (!href) return null;
        return {
            href: parseHref(href),
            rawHref: href,
            ...getAdditionalLinkParams(link),
            type: "url"
        };
    }
    if (link.linktype === "email") {
        const href = `mailto:${link.email}`;
        return {
            href,
            rawHref: href,
            email: link.email,
            ...getAdditionalLinkParams(link),
            type: "email"
        };
    }
    return null;
}
// Helpers
function getAdditionalLinkParams(link) {
    return {
        target: link.target,
        linkType: link.linktype
    };
}
function parseHref(href) {
    if (href.startsWith("/")) {
        return href;
    }
    if (href.startsWith("http")) {
        return href;
    }
    if (href.startsWith("#")) {
        return href;
    }
    return `/${href}`;
}

const resolveId = (id)=>{
    if (!id) return;
    if (typeof id === "string") return id;
    return id.uuid;
};
const imageExtensions = [
    ".gif",
    ".jpg",
    ".jpeg",
    ".png",
    ".webp",
    ".svg",
    ".jpeg"
];
const videoExtensions = [
    ".mpg",
    ".mp2",
    ".mpeg",
    ".mpe",
    ".mpv",
    ".mp4",
    ".webm",
    ".mov",
    ".avi"
];
const getMedia = (blok)=>{
    if (!blok || !blok.filename) return null;
    if (imageExtensions.some((ext)=>blok.filename.endsWith(ext))) {
        const url = blok.filename;
        const pathname = new URL(url).pathname;
        const resolution = pathname.split("/")[3];
        const width = resolution == null ? void 0 : resolution.split("x")[0];
        const height = resolution == null ? void 0 : resolution.split("x")[1];
        return {
            url,
            alt: blok.alt,
            width: parseInt(width || "0"),
            height: parseInt(height || "0"),
            type: "image"
        };
    }
    if (videoExtensions.some((ext)=>blok.filename.endsWith(ext))) {
        return {
            url: blok.filename,
            alt: blok.alt,
            width: 0,
            height: 0,
            type: "video"
        };
    }
    return null;
};

const hasMetadata = (story)=>{
    var _story_content;
    const metadata = story == null ? void 0 : (_story_content = story.content) == null ? void 0 : _story_content.metadata;
    return metadata && Object.keys(metadata).length > 0 && !!metadata.title;
};
function createMetadata({ site, siteName, getTitle }) {
    const generateStoryMetadata = async (story, additionalMetadata = {})=>{
        if (!story) return additionalMetadata;
        const defaultTitle = getTitle ? getTitle(story.name) : siteName ? `${story.name} | ${siteName}` : story.name;
        if (!hasMetadata(story)) {
            // Todo - Notify content editor of missing metadata
            return defu(additionalMetadata, {
                title: defaultTitle
            });
        }
        const metadata = omitFalsy(story.content.metadata);
        var _metadata_title;
        const title = (_metadata_title = metadata.title) != null ? _metadata_title : defaultTitle;
        var _metadata_og_title, _metadata_og_description, _metadata_twitter_title;
        return defu({
            metadataBase: new URL(site),
            title,
            description: metadata.description,
            keywords: story.tag_list.length > 0 ? story.tag_list : undefined,
            openGraph: {
                title: (_metadata_og_title = metadata.og_title) != null ? _metadata_og_title : title,
                description: (_metadata_og_description = metadata.og_description) != null ? _metadata_og_description : metadata.description,
                siteName: siteName,
                images: metadata.og_image ? [
                    {
                        url: metadata.og_image
                    }
                ] : undefined
            },
            twitter: {
                title: (_metadata_twitter_title = metadata.twitter_title) != null ? _metadata_twitter_title : title,
                description: metadata.twitter_description,
                images: metadata.twitter_image ? [
                    {
                        url: metadata.twitter_image
                    }
                ] : metadata.og_image ? [
                    {
                        url: metadata.og_image
                    }
                ] : undefined
            }
        }, additionalMetadata);
    };
    return generateStoryMetadata;
}

export { createMetadata, getLink, getMedia, hasMetadata, resolveId };
